import { Link } from 'react-router-dom';
import { Eye, Download, Trash2, Loader2, User, HardDrive, Clock } from 'lucide-react';
import { formatDateTimeISO, formatDateISO, formatFileSize } from '../../lib/dateUtils';
import type { FileWithSession } from '../../hooks/useFiles';

interface Props {
  file: FileWithSession;
  canDelete: boolean;
  isDeleting: boolean;
  onDownload: (file: FileWithSession) => void;
  onDelete: (file: FileWithSession) => void;
}

const FILE_ICON: Record<string, string> = {
  audio: '🎵',
  text: '📄',
  transcript: '📄',
  document: '📎',
};

export function FileListItem({ file, canDelete, isDeleting, onDownload, onDelete }: Props) {
  return (
    <div className="p-3 md:p-4 border border-steel/20 rounded-md shadow-sm hover:shadow-md hover:bg-ice/50 transition-all">

      {/* ── Desktop layout ── */}
      <div className="hidden md:flex items-start gap-3">
        {/* File emoji */}
        <span className="text-2xl flex-shrink-0 mt-0.5">{FILE_ICON[file.file_type] ?? '📎'}</span>

        {/* Left: content stacked naturally */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <p className="text-sm font-semibold text-navy truncate">{file.file_name}</p>
          <p className="text-xs text-steel flex items-center gap-1.5">
            <HardDrive size={12} className="flex-shrink-0" />
            <span>{formatFileSize(file.file_size)}</span>
            <span>·</span>
            <Clock size={12} className="flex-shrink-0" />
            <span>{formatDateTimeISO(file.created_at)}</span>
            <span>·</span>
            <User size={12} className="flex-shrink-0" />
            <span>{file.uploaded_by_name}</span>
          </p>
          {file.session_id && (
            <div className="flex flex-wrap gap-1.5">
              <span className="px-2 py-0.5 bg-orca-elevate/10 text-orca-elevate rounded-md text-xs font-medium">
                📝 {formatDateISO(file.session_date)}
              </span>
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md text-xs">
                👤 {file.trainee_name}
              </span>
              <span className="px-2 py-0.5 bg-steel/10 text-steel rounded-md text-xs">
                ✈️ {file.aircraft_type}
              </span>
            </div>
          )}
        </div>

        {/* Right: icons stacked independently */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          {file.session_id ? (
            <Link
              to={`/sessions/${file.session_id}`}
              className="p-1.5 text-orca-elevate hover:bg-orca-elevate/10 rounded-md transition-colors"
              aria-label="View session"
              title="View session"
            >
              <Eye size={18} />
            </Link>
          ) : (
            <span className="p-1.5 text-steel/20"><Eye size={18} /></span>
          )}
          <button
            onClick={() => onDownload(file)}
            className="p-1.5 text-orca-elevate hover:bg-orca-elevate/10 rounded-md transition-colors"
            aria-label="Download"
            title="Download"
          >
            <Download size={18} />
          </button>
          {canDelete ? (
            <button
              onClick={() => onDelete(file)}
              disabled={isDeleting}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
              aria-label="Delete"
              title="Delete"
            >
              {isDeleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
            </button>
          ) : (
            <span className="p-1.5 text-steel/20"><Trash2 size={18} /></span>
          )}
        </div>
      </div>

      {/* ── Mobile layout ── */}
      <div className="md:hidden flex items-start gap-3">
        <span className="text-2xl flex-shrink-0 mt-0.5">{FILE_ICON[file.file_type] ?? '📎'}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-navy truncate">{file.file_name}</p>
          <div className="flex flex-col gap-0.5 text-xs text-steel mt-0.5">
            <span className="flex items-center gap-1"><HardDrive size={12} className="flex-shrink-0" />{formatFileSize(file.file_size)}</span>
            <span className="flex items-center gap-1"><Clock size={12} className="flex-shrink-0" />{formatDateTimeISO(file.created_at)}</span>
            <span className="flex items-center gap-1"><User size={12} className="flex-shrink-0" />{file.uploaded_by_name}</span>
          </div>
          {file.session_id && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="px-2 py-0.5 bg-orca-elevate/10 text-orca-elevate rounded-md text-xs font-medium">
                📝 {formatDateISO(file.session_date)}
              </span>
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md text-xs">
                👤 {file.trainee_name}
              </span>
              <span className="px-2 py-0.5 bg-steel/10 text-steel rounded-md text-xs">
                ✈️ {file.aircraft_type}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Mobile footer — icon actions */}
      <div className="md:hidden mt-3 pt-2 border-t border-steel/10 flex items-center justify-around">
        {file.session_id ? (
          <Link
            to={`/sessions/${file.session_id}`}
            className="p-2 text-orca-elevate hover:bg-orca-elevate/10 rounded-md transition-colors"
            aria-label="View session"
          >
            <Eye size={20} />
          </Link>
        ) : (
          <span className="p-2 text-steel/20"><Eye size={20} /></span>
        )}
        <button
          onClick={() => onDownload(file)}
          className="p-2 text-orca-elevate hover:bg-orca-elevate/10 rounded-md transition-colors"
          aria-label="Download"
        >
          <Download size={20} />
        </button>
        {canDelete ? (
          <button
            onClick={() => onDelete(file)}
            disabled={isDeleting}
            className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
            aria-label="Delete"
          >
            {isDeleting ? <Loader2 size={20} className="animate-spin" /> : <Trash2 size={20} />}
          </button>
        ) : (
          <span className="p-2 text-steel/20"><Trash2 size={20} /></span>
        )}
      </div>
    </div>
  );
}
